import toDoListRepository from "../../repositories/toDoListRepository";
import moment from "moment";
import dbRepository from "../../repositories/dbRepository";
import { Toast, Modal } from "bootstrap";
import notifications from "../../helpers/notifications";
import repeatingEventHelper from "../../helpers/repeatingEvents.js";
import languageHelper from "../../helpers/languageHelper.js";
import repeatingEventRepository from "../../repositories/repeatingEventRepository";
import linkifyStr from "linkify-string";
import ClickHandler from "@manuelernestog/click-handler";
import tasksHelper from "../../helpers/tasksHelper";

function afterTodoWrite(promise, fn) {
  Promise.resolve(promise).then((result) => {
    let ok = false;
    try {
      ok = require("../../helpers/todoActivityLog.js").isTodoWriteOk(result);
    } catch (_) {
      ok = false;
    }
    if (!ok) return;
    fn();
  }, () => {});
}

/** Serialize detail writes so concurrent color/time/notes edits do not 409 and drop activity logs. */
function enqueueTodoListWrite(vm, writeFn) {
  if (!vm || typeof writeFn !== "function") return Promise.resolve({ ok: false });
  const prev = vm._todoWriteChain || Promise.resolve();
  const next = prev.then(
    () => writeFn(),
    () => writeFn()
  );
  // Keep the chain alive even if a write rejects.
  vm._todoWriteChain = next.then(
    () => {},
    () => {}
  );
  return next;
}

function afterTodoDelete(promise, fn) {
  Promise.resolve(promise).then((result) => {
    let ok = false;
    try {
      ok = require("../../helpers/todoActivityLog.js").isTodoDeleteWriteOk(result);
    } catch (_) {
      ok = false;
    }
    if (!ok) return;
    fn();
  }, () => {});
}

function beginUndoNotesCascade(todoId) {
  try {
    return require("../../helpers/todoActivityLog.js").planUndoableNotesCascade(todoId);
  } catch (_) {
    return { commit() {} };
  }
}

function showTaskRemovedToast() {
  const toastEl = document.getElementById("taskRemoved");
  if (!toastEl) return 0;
  const toast = new Toast(toastEl);
  const configured = toast._config && toast._config.delay;
  toast.show();
  if (typeof configured === "number" && configured >= 0) return configured;
  try {
    return require("../../helpers/todoActivityLog.js").UNDO_TOAST_MS;
  } catch (_) {
    return 5000;
  }
}

function commitUndoNotesCascade(plan, delayMs, readList) {
  try {
    const { deleteNotesByTodoId } = require("../../sync/engine");
    const { shouldCascadeTodoNotes } = require("../../helpers/todoActivityLog.js");
    plan.commit({
      deleteFn: (todoId) => {
        const list = typeof readList === "function" ? readList() : readList;
        if (!shouldCascadeTodoNotes(list, todoId)) return;
        return deleteNotesByTodoId(todoId);
      },
      delayMs,
    });
  } catch (_) {
    /* cascade must not block the todo delete */
  }
}

function listActivityLabel(vm, listId) {
  const id = listId == null ? "" : String(listId);
  if (/^\d{8}$/.test(id)) return moment(id, "YYYYMMDD").format("YYYY-MM-DD");
  const rows = (vm.$store && vm.$store.getters && vm.$store.getters.cTodoListIds) || [];
  for (let i = 0; i < rows.length; i++) {
    if (rows[i] && String(rows[i].listId) === id && rows[i].listName) return rows[i].listName;
  }
  return id;
}

function noneLabel(vm) {
  return vm.$t("todoDetails.none");
}

function colorActivityLabel(vm, color) {
  if (!color || color === "none") return noneLabel(vm);
  return String(color);
}

function timeActivityLabel(vm, time) {
  if (!time) return noneLabel(vm);
  return moment(time, "HH:mm").format("HH:mm");
}

function reminderActivityLabel(vm, alarm) {
  return alarm ? vm.$t("todoDetails.alarmOn") : vm.$t("todoDetails.alarmOff");
}

function repeatingActivityLabel(vm, repeatingEventId) {
  if (!repeatingEventId) return vm.$t("todoDetails.noRepeat");
  const re =
    vm.$store &&
    vm.$store.getters &&
    vm.$store.getters.repeatingEventList &&
    vm.$store.getters.repeatingEventList[repeatingEventId];
  if (!re) return vm.$t("ui.recurringTasks");
  const typeMap = {
    0: "todoDetails.yearly",
    1: "todoDetails.monthly",
    2: "todoDetails.weekly",
    3: "todoDetails.daily",
    4: "todoDetails.weekdays",
    5: "todoDetails.customWeekdays",
    6: "todoDetails.daysOfMonth",
  };
  const key = typeMap[re.type];
  return key ? vm.$t(key) : vm.$t("ui.recurringTasks");
}

function titleActivityLabel(vm, text) {
  const trimmed = text == null ? "" : String(text).trim();
  return trimmed || vm.$t("activity.untitled");
}

function notesSummary(text) {
  try {
    return require("../../helpers/todoActivityLog.js").summarizeNoteText(text);
  } catch (_) {
    const raw = String(text || "").replace(/\s+/g, " ").trim();
    return raw.length > 40 ? `${raw.slice(0, 40)}…` : raw;
  }
}

function logActivity(vm, type, todo, listId, meta) {
  try {
    const { logTodoActivity } = require("../../helpers/todoActivityLog.js");
    const { createNote } = require("../../sync/engine");
    const pending = logTodoActivity({
      type,
      todo,
      listId,
      meta,
      t: vm.$t.bind(vm),
      createNoteFn: createNote,
    });
    if (pending && typeof pending.then === "function") pending.then(() => {}, () => {});
  } catch (err) {
    /* activity log must not block the todo write */
  }
}

export default {
  data() {
    return {
      pickedDate: new Date(),
      pickedCList: "",
      pickedCListName: "",
      cListOptions: [],
      todo: {
        text: "",
        checked: false,
        desc: "",
        subTaskList: [],
        alarm: false,
      },
      todoList: null,
      index: 0,
      newSubTask: { text: "", checked: false, editing: false },
      tempTitle: "",
      committedTitle: "",
      tempSubTask: "",
      editingTitle: false,
      showingCalendar: true,
      loadingView: false,
      options: { target: "_blank", defaultProtocol: "https" },
      clickhandler: new ClickHandler(),
    };
  },
  methods: {
    applySelectedTodo(newVal) {
      if (!newVal || !newVal.toDo) return;
      this.todoList = this.$store.getters.todoLists[newVal.toDo.listId];
      if (!this.todoList) return;
      this.index = newVal.index;
      this.todo = this.todoList[this.index];
      if (!this.todo) return;
      if (this.todo["desc"] == undefined) {
        this.todo["desc"] = "";
        this.todo["subTaskList"] = [];
        this.todo["color"] = "none";
        this.todo["priority"] = 0;
        this.todo["tags"] = [];
        this.todo["time"] = null;
        this.todo["alarm"] = false;
        this.todo["repeatingEvent"] = null;
      }
      this.committedTitle = this.todo.text || "";
      this.tempTitle = this.committedTitle;
      this.showingCalendar = moment(this.todo.listId, "YYYYMMDD", true).isValid();
      this.getCListOptions();
      this.loadingView = true;
      if (this.showingCalendar) {
        this.pickedDate = moment(this.todo.listId).toDate();
        this.pickedCList = "";
        this.pickedCListName = "";
      } else {
        this.cListOptions.forEach((x) => {
          if (x.listId == this.todo.listId) {
            this.pickedCListName = x.listName;
          }
        });
        this.pickedCList = this.todo.listId;
        this.pickedDate = null;
      }
      this.$nextTick(() => {
        this.loadingView = false;
      });
    },
    removeSubTask(index) {
      const removed = this.todo.subTaskList[index];
      const subtaskText = removed && removed.text;
      this.todo.subTaskList.splice(index, 1);
      const listId = this.todo && this.todo.listId;
      const activityTodo = this.todo ? { id: this.todo.id, text: this.todo.text } : null;
      afterTodoWrite(this.updateTodo(), () => {
        logActivity(this, "subtask_removed", activityTodo, listId, {
          subtask: titleActivityLabel(this, subtaskText),
        });
      });
    },
    addSubTask() {
      const addedText = this.newSubTask.text;
      if (addedText != "") {
        this.todo.subTaskList.push({
          text: addedText,
          checked: false,
          editing: false,
        });
        this.newSubTask.text = "";
      }
      const listId = this.todo && this.todo.listId;
      const activityTodo = this.todo ? { id: this.todo.id, text: this.todo.text } : null;
      afterTodoWrite(this.updateTodo(), () => {
        if (!String(addedText || "").trim()) return;
        logActivity(this, "subtask_added", activityTodo, listId, {
          subtask: titleActivityLabel(this, addedText),
        });
      });
    },
    cancelAddSubTask() {
      this.newSubTask.text = "";
      if (this.$refs["newSubTask"]) this.$refs["newSubTask"].blur();
    },
    editSubTask(index) {
      this.todo.subTaskList[index].editing = true;
      this.$nextTick(() => {
        const ref = this.$refs["subTaskEdit" + index];
        const input = Array.isArray(ref) ? ref[0] : ref;
        if (input) {
          input.focus();
          input.select();
        }
        this.tempSubTask = this.todo.subTaskList[index].text;
      });
    },
    doneEditSubTask(index) {
      this.todo.subTaskList[index].editing = false;
      const from = this.tempSubTask;
      const to = this.todo.subTaskList[index].text;
      const listId = this.todo && this.todo.listId;
      const activityTodo = this.todo ? { id: this.todo.id, text: this.todo.text } : null;
      afterTodoWrite(this.updateTodo(), () => {
        if (String(from || "").trim() === String(to || "").trim()) return;
        logActivity(this, "subtask_renamed", activityTodo, listId, {
          from: titleActivityLabel(this, from),
          to: titleActivityLabel(this, to),
        });
      });
    },
    cancelEditSubTask(index) {
      this.todo.subTaskList[index].text = this.tempSubTask;
      const ref = this.$refs["subTaskEdit" + index];
      const input = Array.isArray(ref) ? ref[0] : ref;
      if (input) input.blur();
    },
    editTitle(e) {
      if (e && e.target && e.target.closest && e.target.closest("a")) return;
      this.editingTitle = true;
      this.$nextTick(() => {
        this.tempTitle = this.todo.text;
        if (this.$refs["titleInput"]) {
          this.$refs["titleInput"].focus();
          this.$refs["titleInput"].select();
        }
      });
    },
    onTitleFocus() {
      this.editingTitle = true;
      this.tempTitle = this.todo ? this.todo.text : "";
    },
    onTitleEnter(e) {
      if (e && (e.isComposing || e.keyCode === 229)) return;
      this.doneEditTitle();
    },
    cancelEditTitle() {
      this.todo.text = this.tempTitle;
      if (this.$refs["titleInput"]) this.$refs["titleInput"].blur();
    },
    doneEditTitle() {
      this.editingTitle = false;
      // Use last committed title, not tempTitle — autofill/re-focus can overwrite tempTitle
      // with the already-edited value and skip the activity log.
      const from = this.committedTitle;
      const to = this.todo && this.todo.text;
      const listId = this.todo && this.todo.listId;
      const activityTodo = this.todo ? { id: this.todo.id, text: this.todo.text } : null;
      afterTodoWrite(this.updateTodo(), () => {
        if (String(from || "").trim() === String(to || "").trim()) return;
        this.committedTitle = to == null ? "" : String(to);
        logActivity(this, "title_changed", activityTodo, listId, {
          from: titleActivityLabel(this, from),
          to: titleActivityLabel(this, to),
        });
      });
    },
    toggleTodoChecked() {
      this.todo.checked = !this.todo.checked;
      this.checkTodo(false);
    },
    startDrag(event, index) {
      event.dataTransfer.setData("index", index);
    },
    onDragenter(event) {
      event.target.parentElement.classList.add("drag-hover");
    },
    onDragleave(event) {
      event.target.parentElement.classList.remove("drag-hover");
    },
    onDrop(event, to_index) {
      let from_index = event.dataTransfer.getData("index");
      let sub_task = this.todo.subTaskList.splice(parseInt(from_index), 1)[0];
      this.todo.subTaskList.splice(to_index, 0, sub_task);
      event.target.parentElement.classList.remove("drag-hover");
      this.updateTodo();
    },
    showCalendar() {
      const el = document.getElementById("todo-date-picker-input");
      if (el) el.focus();
    },
    checkTodoClickhandler(resetRepeatinEvent = true) {
      this.clickhandler.handle(
        function () {
          this.checkTodo(resetRepeatinEvent);
        }.bind(this),
        function () {}
      );
    },
    checkTodo(resetRepeatinEvent = true) {
      const todo = this.todo;
      const listId = todo && todo.listId;
      const activityType = todo && todo.checked ? "completed" : "uncompleted";
      const activityTodo = todo ? { id: todo.id, text: todo.text } : null;
      const written = this.updateTodoWithReorder(resetRepeatinEvent);
      if (this.todoList && this.todo) {
        const next = this.todoList.indexOf(this.todo);
        if (next >= 0) this.index = next;
      }
      afterTodoWrite(written, () => {
        logActivity(this, activityType, activityTodo, listId);
      });
    },
    updateTodo(resetRepeatinEvent = true) {
      if (resetRepeatinEvent) {
        this.todo.repeatingEvent = null;
      }
      const { touchTodo } = require("../../sync/todoMerge.js");
      if (this.todo) touchTodo(this.todo);
      return this.updateTodoList(this.todo.listId, this.todoList);
    },
    updateTodoWithReorder(resetRepeatinEvent = true) {
      if (resetRepeatinEvent) {
        this.todo.repeatingEvent = null;
      }
      const { touchTodo } = require("../../sync/todoMerge.js");
      if (this.todo) touchTodo(this.todo);
      tasksHelper.applyListOrder(this.todoList, this.$store.getters.config);
      return this.updateTodoList(this.todo.listId, this.todoList);
    },
    updateTodoList(todoListId, TodoList) {
      notifications.refreshDayNotifications(this, todoListId);
      return enqueueTodoListWrite(this, () => toDoListRepository.update(todoListId, TodoList));
    },
    getCListOptions() {
      const { activeCustomLists } = require("../../helpers/customListArchive.js");
      this.cListOptions = activeCustomLists(this.$store.getters.cTodoListIds);
    },
    moveToTodoList(newListID) {
      if (newListID == "Invalid date" || newListID == "") return;

      if (moment(newListID, "YYYYMMDD", true).isValid()) {
        this.pickedCListName = "";
        this.pickedCList = "";
      } else {
        this.cListOptions.forEach((x) => {
          if (x.listId == this.pickedCList) {
            this.pickedCListName = x.listName;
          }
        });
      }

      const oldListId = this.todo.listId;
      const todo = this.todo;
      const shouldLog = String(oldListId) !== String(newListID);
      const meta = {
        fromLabel: listActivityLabel(this, oldListId),
        toLabel: listActivityLabel(this, newListID),
      };
      this.todoList.splice(this.index, 1);
      const oldWrite = this.updateTodoList(oldListId, this.todoList);
      this.todo.listId = newListID;
      this.todo.repeatingEvent = null;
      if (this.$store.getters.todoLists[newListID]) {
        const moved = this.todo;
        this.$store.commit("addTodo", moved);
        this.todoList = this.$store.getters.todoLists[this.todo.listId];
        tasksHelper.applyListOrder(this.todoList, this.$store.getters.config);
        const newWrite = this.updateTodoList(newListID, this.todoList);
        this.index = this.todoList.indexOf(moved);
        this.todo = moved;
        if (shouldLog) {
          afterTodoWrite(Promise.all([oldWrite, newWrite]), () => {
            logActivity(this, "moved", todo, newListID, meta);
          });
        }
      } else {
        this.loadToDoFormDB(newListID, shouldLog ? { todo, meta, priorWrite: oldWrite } : null);
      }
    },
    loadToDoFormDB(newListID, activity) {
      let db_req = dbRepository.open();
      var instancePointer = this;
      db_req.onsuccess = function (event) {
        let db = event.target.result;
        var get_req = dbRepository.get(db, "todo_lists", newListID);
        get_req.onsuccess = function (event) {
          let newTodoList = event.target.result ? event.target.result : [];
          newTodoList.push(instancePointer.todo);
          instancePointer.todoList = newTodoList;
          instancePointer.index = newTodoList.length - 1;
          const written = this.updateTodoList(newListID, instancePointer.todoList);
          if (activity) {
            afterTodoWrite(Promise.all([activity.priorWrite, written]), () => {
              logActivity(this, "moved", activity.todo, newListID, activity.meta);
            });
          }
        }.bind(this);
      }.bind(this);
    },
    removeTodo() {
      const listId = this.todo.listId;
      const todoId = this.todo && this.todo.id;
      if (this.todo && this.todo.repeatingEvent) {
        const repeatingEventHelper = require("../../helpers/repeatingEvents.js").default;
        repeatingEventHelper.markDayHandled(this, listId, this.todo.repeatingEvent);
      }
      this.$store.commit("setUndoElement", {
        type: "task",
        todo: this.todo,
        index: this.index,
      });
      this.$store.commit("removeTodo", {
        toDoListId: listId,
        index: this.index,
      });
      const written = this.updateTodoList(listId, this.$store.getters.todoLists[listId]);
      const notesCascade = beginUndoNotesCascade(todoId);
      const undoDelay = showTaskRemovedToast();
      afterTodoDelete(written, () =>
        commitUndoNotesCascade(notesCascade, undoDelay, () => this.$store.getters.todoLists[listId])
      );
      if (typeof this.onTodoRemoved === "function") this.onTodoRemoved();
    },
    undoRemoveTask() {
      let obj = this.$store.getters.undoElement;
      if (obj && obj.todo && obj.todo.id) {
        try {
          require("../../helpers/todoActivityLog.js").cancelScheduledTodoNotesCascade(obj.todo.id);
        } catch (_) {
          /* undo must still restore the task */
        }
      }
      this.$store.commit("insertTodo", {
        toDoListId: obj.todo.listId,
        index: obj.index,
        toDo: obj.todo,
      });
      this.updateTodoList(obj.todo.listId, this.$store.getters.todoLists[obj.todo.listId]);
      const toastEl = document.getElementById("taskRemoved");
      if (toastEl) new Toast(toastEl).hide();
    },
    getRemoveConfirmModalId() {
      return "removeReModalToDoDetails";
    },
    removeAll() {
      const el = document.getElementById(this.getRemoveConfirmModalId());
      if (!el) return;
      new Modal(el, { backdrop: "static" }).show();
    },
    removeAllComfirmed() {
      const reId = this.todo && this.todo.repeatingEvent;
      if (!reId) return;
      repeatingEventRepository.remove(reId);
      this.$store.commit("removeRepeatingEvent", reId);
      const days = new Set(this.$store.getters.selectedDates || []);
      const cache = this.$store.getters.repeatingEventDateCache || {};
      for (const day of Object.keys(cache)) {
        if (Array.isArray(cache[day]) && cache[day].includes(reId)) days.add(day);
      }
      for (const day of Object.keys(this.$store.getters.todoLists || {})) {
        if (/^\d{8}$/.test(day)) days.add(day);
      }
      days.forEach((date) => {
        repeatingEventHelper.removeGeneratedRepeatingEvents(date, this);
      });
      this.$store.commit("resetRepeatingEventDateCache");
      this.$store.commit("loadRepeatingEventDateCache", this.$store.getters.repeatingEventList);
      const toastEl = document.getElementById("recurrentTaskRemoved");
      if (toastEl) new Toast(toastEl).show();
      if (typeof this.onTodoRemoved === "function") this.onTodoRemoved();
    },
    removeAllCanceled() {
      if (typeof this.onRemoveAllCanceled === "function") {
        this.onRemoveAllCanceled();
        return;
      }
      const el = document.getElementById("toDoModal");
      if (el) new Modal(el).show();
    },
    async copyTodo() {
      await navigator.clipboard.writeText(this.todoToString());
      const toastEl = document.getElementById("copiedTaskToClipboard");
      if (toastEl) new Toast(toastEl).show();
    },
    todoToString() {
      var text = "";
      text += this.todo.text;
      if (this.todo.desc != "") {
        text += "\n\n";
        text += this.$t("todoDetails.notes") + ":\n\n";
        text += this.todo.desc;
      }
      if (this.todo.subTaskList.length > 0) {
        text += "\n\n";
        text += this.$t("todoDetails.subtasks") + ":\n\n";
        this.todo.subTaskList.forEach(function (task) {
          text += "- " + task.text + "\n";
        });
      }
      return text;
    },
    changeColor(color) {
      const from = this.todo.color;
      const to = color;
      this.todo.color = color;
      const listId = this.todo && this.todo.listId;
      const activityTodo = this.todo ? { id: this.todo.id, text: this.todo.text } : null;
      afterTodoWrite(this.updateTodo(), () => {
        if (String(from || "none") === String(to || "none")) return;
        logActivity(this, "color_changed", activityTodo, listId, {
          from: colorActivityLabel(this, from),
          to: colorActivityLabel(this, to),
        });
      });
    },
    changeTime(time) {
      const fromTime = this.todo.time;
      const fromAlarm = !!this.todo.alarm;
      this.todo.time = time;
      if (!time) {
        this.todo.alarm = false;
      }
      const listId = this.todo && this.todo.listId;
      const activityTodo = this.todo ? { id: this.todo.id, text: this.todo.text } : null;
      afterTodoWrite(this.updateTodoWithReorder(), () => {
        if (String(fromTime || "") !== String(time || "")) {
          logActivity(this, "time_changed", activityTodo, listId, {
            from: timeActivityLabel(this, fromTime),
            to: timeActivityLabel(this, time),
          });
        }
        if (fromAlarm && !this.todo.alarm) {
          logActivity(this, "reminder_changed", activityTodo, listId, {
            from: reminderActivityLabel(this, true),
            to: reminderActivityLabel(this, false),
          });
        }
      });
    },
    changeAlarm() {
      if (!this.todo.time) return;
      const from = !!this.todo.alarm;
      this.todo.alarm = !from;
      const to = !!this.todo.alarm;
      const listId = this.todo && this.todo.listId;
      const activityTodo = this.todo ? { id: this.todo.id, text: this.todo.text } : null;
      afterTodoWrite(this.updateTodo(), () => {
        logActivity(this, "reminder_changed", activityTodo, listId, {
          from: reminderActivityLabel(this, from),
          to: reminderActivityLabel(this, to),
        });
      });
    },
    changeDescription(desc) {
      const from = this.todo.desc || "";
      const to = desc || "";
      this.todo.desc = desc;
      const listId = this.todo && this.todo.listId;
      const activityTodo = this.todo ? { id: this.todo.id, text: this.todo.text } : null;
      afterTodoWrite(this.updateTodo(), () => {
        if (String(from) === String(to)) return;
        if (!String(to).trim()) {
          logActivity(this, "notes_cleared", activityTodo, listId);
          return;
        }
        logActivity(this, "notes_changed", activityTodo, listId, {
          summary: notesSummary(to),
        });
      });
    },
    changeRepeatingEvent(repeatingEvent) {
      const from = this.todo.repeatingEvent;
      const to = repeatingEvent;
      this.todo.repeatingEvent = repeatingEvent;
      const listId = this.todo && this.todo.listId;
      const activityTodo = this.todo ? { id: this.todo.id, text: this.todo.text } : null;
      afterTodoWrite(this.updateTodo(false), () => {
        if (String(from || "") === String(to || "")) return;
        logActivity(this, "repeat_changed", activityTodo, listId, {
          from: repeatingActivityLabel(this, from),
          to: repeatingActivityLabel(this, to),
        });
      });
    },
    changeSubTaskClickhandler(index) {
      this.clickhandler.handle(
        function () {
          this.changeSubTask(index);
        }.bind(this),
        function () {
          this.editSubTask(index);
        }.bind(this),
        index
      );
    },
    toggleSubTask(index) {
      const item = this.todo.subTaskList[index];
      if (!item) return;
      item.checked = !item.checked;
      this.changeSubTask(index);
    },
    changeSubTask(index) {
      const item = this.todo.subTaskList[index];
      if (item && item.checked && this.moveSubtaskToBotttom) {
        this.todo.subTaskList.push(this.todo.subTaskList.splice(index, 1)[0]);
      }
      const listId = this.todo && this.todo.listId;
      const activityTodo = this.todo ? { id: this.todo.id, text: this.todo.text } : null;
      const checked = !!(item && item.checked);
      const subtaskText = item && item.text;
      afterTodoWrite(this.updateTodo(), () => {
        logActivity(
          this,
          checked ? "subtask_completed" : "subtask_uncompleted",
          activityTodo,
          listId,
          { subtask: titleActivityLabel(this, subtaskText) }
        );
      });
    },
    linkifyText(text) {
      return linkifyStr(text, this.options);
    },
  },
  computed: {
    language() {
      let lang = this.$store.getters.config.language;
      return languageHelper.getLanguagePack(lang);
    },
    showCL() {
      return this.$store.getters.config.customList;
    },
    showCal() {
      return this.$store.getters.config.calendar;
    },
    todoText() {
      return linkifyStr(this.todo.text, this.options);
    },
    moveSubtaskToBotttom() {
      return this.$store.getters.config.moveCompletedSubTaskToBottom;
    },
    weekStartOnMonday() {
      return this.$store.getters.config.weekStartOnMonday ? 1 : 0;
    },
    listTitle() {
      if (this.showingCalendar && this.todo && this.todo.listId) {
        return moment(this.todo.listId).format("MMM D");
      }
      return this.pickedCListName || "";
    },
  },
};
